import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function useAuthState() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  return { user, setUser, loading, setLoading };
}

function useAuthActions(setUser, setLoading) {
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (_) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, [setUser]);

  const register = useCallback(async (email, password, name) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, name }, { withCredentials: true });
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Logout API failure is non-critical — local state cleared in finally
      void err;
    } finally {
      setUser(false);
    }
  }, [setUser]);

  return { checkAuth, login, register, logout };
}

export function AuthProvider({ children }) {
  const { user, setUser, loading, setLoading } = useAuthState();
  const { checkAuth, login, register, logout } = useAuthActions(setUser, setLoading);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth }),
    [user, loading, login, register, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
